const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

export type PersistedCalendarVisibility = {
  calendarHandle?: string;
  visibleCalendarIds?: string[];
  defaultCalendarId?: string;
};

const readCalendarObject = (config: unknown): Record<string, unknown> | undefined => {
  if (!isPlainObject(config)) return undefined;
  const nested = isPlainObject(config.config) ? config.config : config;
  const calendar = nested.calendar;
  return isPlainObject(calendar) ? calendar : undefined;
};

export const readPersistedCalendarVisibility = (
  config: unknown,
): PersistedCalendarVisibility => {
  const calendar = readCalendarObject(config);
  if (!calendar) return {};

  const event = isPlainObject(calendar.event) ? calendar.event : {};
  const calendarHandle =
    typeof event.calendar === "string" && event.calendar.trim()
      ? event.calendar.trim()
      : undefined;

  const visibleCalendarIds = Array.isArray(calendar.visibleCalendarIds)
    ? calendar.visibleCalendarIds.filter(
        (id): id is string => typeof id === "string" && id.length > 0,
      )
    : undefined;
  const defaultCalendarId =
    typeof calendar.defaultCalendarId === "string" &&
    calendar.defaultCalendarId.trim()
      ? calendar.defaultCalendarId.trim()
      : undefined;

  return {
    ...(calendarHandle ? { calendarHandle } : {}),
    ...(visibleCalendarIds ? { visibleCalendarIds } : {}),
    ...(defaultCalendarId ? { defaultCalendarId } : {}),
  };
};

export const findPersistedCalendarVisibility = (
  blocks?: Array<{ default?: boolean; config?: unknown } | null | undefined>,
): PersistedCalendarVisibility => {
  const list = (blocks ?? []).filter(
    (block): block is { default?: boolean; config?: unknown } => Boolean(block),
  );
  const withCalendar = list.find((block) => {
    const visibility = readPersistedCalendarVisibility(block.config);
    return Boolean(visibility.calendarHandle || visibility.visibleCalendarIds);
  });
  const fallback = list.find((block) => block.default) ?? list[0];
  return readPersistedCalendarVisibility((withCalendar ?? fallback)?.config);
};

export const readPersistedCalendarFieldMap = (
  config: unknown,
): Record<string, string> | undefined => {
  const calendar = readCalendarObject(config);
  if (!calendar || !isPlainObject(calendar.calendarFieldMap)) return undefined;
  const map = Object.fromEntries(
    Object.entries(calendar.calendarFieldMap).filter(
      (entry): entry is [string, string] =>
        typeof entry[0] === "string" &&
        typeof entry[1] === "string" &&
        entry[1].length > 0,
    ),
  );
  return Object.keys(map).length > 0 ? map : undefined;
};
